import { Box, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Icon, IconName, useDeviceTypes } from "halifax";
import {
  FlightSelectors,
  useCheckoutStateSelector,
  LodgingSelectors,
} from "@capone/checkout";
import dayjs from "dayjs";
import clsx from "clsx";
import styles from "./styles.module.scss";
import { PackageDetailsPanelConnectorProps } from "./container";
import { getShortLabel } from "../../../flight-shop/components/FlightList/components/FlightListInfo/textConstants";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import { ReviewHotelItinerary } from "../../../itinerary-review/components/ReviewHotelItinerary";
import { ReviewFlightItinerary } from "../../../itinerary-review/components/ReviewFlightItinerary";
import { HOTEL, FLIGHT, HOTEL_DETAILS, FLIGHT_DETAILS } from "./constants";
import { ChangeItineraryModal } from "../../../itinerary-review/components/ChangeItineraryModal";

export interface IPackagesDetailsPanelProps
  extends RouteComponentProps,
    PackageDetailsPanelConnectorProps {}

export const PackageCheckoutDetails: React.FC<IPackagesDetailsPanelProps> = ({
  origin,
  destination,
}) => {
  const { matchesMobile } = useDeviceTypes();
  const [openChangeRoomModal, setOpenChangeRoomModal] = useState(false);
  const [openChangeHotelModal, setOpenChangeHotelModal] = useState(false);
  const [openChangeOutboundFlightModal, setOpenChangeOutboundFlightModal] =
    useState(false);

  const expState = useExperiments();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const selectedLodging = useCheckoutStateSelector(
    LodgingSelectors.getSelectedLodging
  );
  const selectedRoom = useCheckoutStateSelector(
    LodgingSelectors.getSelectedRoom
  );
  const selectedRoomProduct = useCheckoutStateSelector(
    LodgingSelectors.getSelectedRoomProduct
  );
  const fromDate = useCheckoutStateSelector(LodgingSelectors.getHotelFromDate);
  const untilDate = useCheckoutStateSelector(
    LodgingSelectors.getHotelUntilDate
  );
  const guestCount = useCheckoutStateSelector(LodgingSelectors.getGuestCount);

  const tripDetails = useCheckoutStateSelector(FlightSelectors.getTripDetails);

  const outgoingSlice = tripDetails.slices?.find((slice) => slice.outgoing);
  const returnSlice = tripDetails.slices?.find((slice) => !slice.outgoing);

  if (isPackageCheckoutOptimizationExperiment) {
    return (
      <>
        <div
          className={clsx(styles["package-checkout-enhanced-details-root"], {
            [styles.mobile]: matchesMobile,
          })}
        >
          <div className={styles["product-heading"]}>
            {matchesMobile ? undefined : (
              <Icon name={IconName.HotelFunnelIcon} />
            )}
            <Typography className={styles["header-text"]}>
              {matchesMobile ? HOTEL_DETAILS : HOTEL}
            </Typography>
          </div>
          <ReviewHotelItinerary
            setOpenChangeHotelModal={setOpenChangeHotelModal}
            setOpenChangeRoomModal={setOpenChangeRoomModal}
            showCardContentIcons={!matchesMobile}
            showSimplifiedCancellationPolicy
          />

          <div className={styles["product-heading"]}>
            {matchesMobile ? undefined : (
              <Icon name={IconName.FlightFunnelIcon} />
            )}
            <Typography className={styles["header-text"]}>
              {matchesMobile ? FLIGHT_DETAILS : FLIGHT}
            </Typography>
          </div>
          <ReviewFlightItinerary
            setOpenChangeOutboundFlightModal={setOpenChangeOutboundFlightModal}
            showChangeCTAs
            showSimplifiedCancellationPolicy
            showFlightShopSummaryPanel
          />
        </div>
        {(openChangeHotelModal ||
          openChangeRoomModal ||
          openChangeOutboundFlightModal) && (
          <ChangeItineraryModal
            type={(() => {
              switch (true) {
                case openChangeHotelModal:
                  return "hotel";
                case openChangeRoomModal:
                  return "room";
                case openChangeOutboundFlightModal:
                default:
                  return "outbound-flight";
              }
            })()}
            openModal={
              openChangeHotelModal ||
              openChangeRoomModal ||
              openChangeOutboundFlightModal
            }
            setOpenModal={(() => {
              switch (true) {
                case openChangeHotelModal:
                  return setOpenChangeHotelModal;
                case openChangeRoomModal:
                  return setOpenChangeRoomModal;
                case openChangeOutboundFlightModal:
                default:
                  return setOpenChangeOutboundFlightModal;
              }
            })()}
            isMobile={matchesMobile}
          />
        )}
      </>
    );
  }

  return (
    <Box className={styles["package-details-panel-root"]}>
      <Box className={styles["package-details-panel-section"]}>
        <Box className={styles["package-details-panel-section-icon-wrapper"]}>
          <Box className={styles["icon-circle"]}>
            <Icon name={IconName.HotelFunnelIcon} />
          </Box>
        </Box>
        <Box
          className={styles["package-details-panel-section-details-wrapper"]}
        >
          <Typography className={styles["package-details-panel-section-name"]}>
            <strong>
              {selectedLodging?.lodging.name}, {selectedLodging?.lodging.city}
            </strong>
          </Typography>
          <Box className={styles["package-details-panel-section-details-row"]}>
            <Typography>{selectedRoom?.roomInfo.name}</Typography>
            <span className={styles.divider}> | </span>
            <Typography>
              Room fits (
              {selectedRoom?.roomInfo.maxAdults ||
                selectedRoomProduct?.bedTypes.occupancy}
              ) guest
              {(selectedRoom?.roomInfo.maxAdults
                ? selectedRoom?.roomInfo.maxAdults
                : selectedRoomProduct?.bedTypes.occupancy) === 1
                ? ""
                : "s"}
            </Typography>
            <span className={styles.divider}> | </span>
            <Typography>
              <strong>Check-in:</strong> {dayjs(fromDate).format("ddd, MMM D")}
            </Typography>
            <span className={styles.divider}> | </span>
            <Typography>
              <strong>Checkout:</strong> {dayjs(untilDate).format("ddd, MMM D")}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={styles["package-details-section-separator"]}>
        <Box className={styles["icon-circle"]}>
          <span>+</span>
        </Box>
      </Box>
      <Box className={styles["package-details-panel-section"]}>
        <Box className={styles["package-details-panel-section-icon-wrapper"]}>
          <Box className={styles["icon-circle"]}>
            <Icon name={IconName.FlightFunnelIcon} />
          </Box>
        </Box>
        <Box
          className={styles["package-details-panel-section-details-wrapper"]}
        >
          <Typography className={styles["package-details-panel-section-name"]}>
            <strong>{getShortLabel(origin?.label || "")}</strong> (
            {outgoingSlice?.originCode}) to{" "}
            <strong>{getShortLabel(destination?.label || "")}</strong> (
            {outgoingSlice?.destinationCode})
          </Typography>
          <Box className={styles["package-details-panel-section-details-row"]}>
            <Typography>
              {guestCount} traveler{guestCount === 1 ? "" : "s"}
            </Typography>
            <span className={styles.divider}> | </span>
            <Typography>Round-trip flight</Typography>
            <span className={styles.divider}> | </span>
            <Typography>
              <strong>Outbound:</strong>{" "}
              {dayjs(outgoingSlice?.departureTime).format("ddd, MMM D")}
            </Typography>
            <span className={styles.divider}> | </span>
            <Typography>
              <strong>Return:</strong>{" "}
              {dayjs(returnSlice?.departureTime).format("ddd, MMM D")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
